// ---------------------------------------------------------------------------
// V22 — ★ THE PROOF PACK.
//
// `08-signature-modules.md` §V22.1: "it turns a dispute into a document. It is what defends
// an invoice, wins a renewal, and answers a regulator."
//
// ---------------------------------------------------------------------------
// WHAT THIS SCREEN IS FOR, WHICH IS NOT BROWSING
//
// Nobody reads a Proof Pack here. They read it six months later, as a PDF attached to a
// letter, or as JSON handed to a verifier. So the screen has three jobs and no others:
// issue one, say whether an issued one still agrees with the source records, and get the
// two files out.
//
// The ten sections are NOT rendered as a document. A skimmable HTML version would become
// the thing people cite, and it is the one artefact of the three that carries no signature
// — §5 is explicit that the PDF is a rendering and the JSON is the truth, and a third,
// unsigned, un-exportable rendering would be worse than either.
//
// ---------------------------------------------------------------------------
// RECOMPUTING IS OFFERED AND IS NEVER AUTOMATIC
//
// `chainAgreement` is careful about what a mismatch means: an event that has carried on
// since a pack was issued HAS newer audit rows, so a disagreement is usually "this pack is
// out of date" rather than "somebody tampered". A screen that ran the check on load and
// painted red would cry forgery on every healthy event, and the third time it did that
// nobody would read it again.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { send } from "../api/auth";
import { console_ } from "../api/console";
import { AddButton } from "../design/AddLink";
import { Notice } from "../design/Notice";
import { StatusChip } from "../design/StatusChip";
import { useT } from "../i18n/LocaleProvider";
import { useSession } from "../session/SessionProvider";
import { chainAgreement, proofPackState, shortHash, type ProofPackRow } from "../system/proof";

type Payload = { signing_available: boolean; rows: ProofPackRow[] };
type Chain = { merkle_root: string; content_sha256: string; leaf_count: number; content_sha256_by_format?: Record<string, string> };

// UTC, DELIBERATELY, AND NOT `system/format`'s `when`. V22 §6.7 requires that a
// pack generated in Budapest and the same pack generated in UTC agree —
// `verify-V22.sh` runs its matrix under both zones and carries a sabotage that
// rewrites `at time zone 'UTC'` and must catch it. So this screen must NOT take
// V27a's `whenIn` and the organisation clock: a signed attestation rendered in
// whoever-happens-to-be-reading's zone is worse, not better. It shadows `when`
// on purpose; the closing list's organisation-clock item skips this file.
function when(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toISOString().replace("T", " ").slice(0, 19);
}

export function ProofPacks() {
  const { id = "" } = useParams();
  const { apiBase } = useSession();
  const { t, refusal } = useT();
  const [data, setData] = useState<Payload | null>(null);
  const [chain, setChain] = useState<Chain | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const res = await send<{ proof_packs?: Payload; error?: { message: string } }>(
      console_.eventProofPacks(apiBase, id),
    );
    if (res.status === 200 && res.body?.proof_packs) {
      setData(res.body.proof_packs);
      setFailed(null);
    } else {
      setFailed(refusal(res.body?.error) ?? t("These Proof Packs could not be read."));
    }
  }, [apiBase, id]);

  useEffect(() => {
    void load();
  }, [load]);

  const recompute = useCallback(async () => {
    setBusy(true);
    const res = await send<{ recomputation?: Chain; error?: { message: string } }>(
      console_.proofChain(apiBase, id),
    );
    setBusy(false);
    if (res.status === 200 && res.body?.recomputation) {
      setChain(res.body.recomputation);
    } else {
      setFailed(refusal(res.body?.error) ?? t("The chain could not be recomputed."));
    }
  }, [apiBase, id]);

  const issue = useCallback(
    async (supersedes: string | null) => {
      setBusy(true);
      const res = await send<{ error?: { message: string } }>(
        console_.issueProofPack(apiBase, id, supersedes),
      );
      setBusy(false);
      if (res.status >= 400) {
        setFailed(refusal(res.body?.error) ?? t("That pack could not be issued."));
        return;
      }
      setChain(null);
      await load();
    },
    [apiBase, id, load],
  );

  if (data === null && failed !== null) {
    return (
      <section className="panel">
        <div className="panel-head">
          <h2>{t("Proof Packs")}</h2>
        </div>
        <StatusChip tone="failed" label={failed} />
      </section>
    );
  }
  if (data === null) {
    return (
      <section className="panel">
        <div className="panel-head">
          <h2>{t("Proof Packs")}</h2>
        </div>
        <p className="muted">{t("Reading the packs issued for this event…")}</p>
      </section>
    );
  }

  const current = data.rows.find((r) => !r.is_superseded) ?? null;

  return (
    <div className="stack">
      <h1>{t("Proof Packs")}</h1>
      <p className="muted">
        {t("A signed record of who was on which post, when they arrived, what they were briefed on, whether their credentials were valid at the time, what changed and who approved it. Immutable once issued; a correction is a new pack.")}
      </p>
      {failed ? <StatusChip tone="failed" label={failed} /> : null}

      {/* THE SCREEN SAYS WHEN IT CANNOT SIGN, rather than drawing a button that 503s.
          No environment holds a production key yet and that is the expected state. */}
      {data.signing_available ? null : (
        <Notice tone="attention">{t("No signing key is configured here, so a pack cannot be issued from this environment.")}</Notice>
      )}

      <section className="panel stack">
        <div className="panel-head">
          <h2>{t("Issued")}</h2>
          <div className="row">
            <button type="button" className="btn btn-quiet" disabled={busy} onClick={() => void recompute()}>
              {t("Recompute the chain from source")}
            </button>
            {data.signing_available ? (
              <AddButton
                label={current ? t("Issue a correcting pack") : t("Issue a Proof Pack")}
                expanded={false}
                onClick={() => (busy ? undefined : void issue(current?.pack_id ?? null))}
              />
            ) : null}
          </div>
        </div>
        {data.rows.length === 0 ? (
          <p className="muted">
            {t("No Proof Pack has been issued for this event yet. One can be issued at any point; issuing it after the last timesheet is approved is what makes it complete.")}
          </p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>{t("Issued")}</th>
                <th>{t("State")}</th>
                <th>{t("Chain")}</th>
                <th>{t("People")}</th>
                <th>{t("Signature")}</th>
                <th>{t("Export")}</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => {
                const s = proofPackState(r, t);
                // ONLY THE CURRENT PACK IS COMPARED AGAINST A RECOMPUTATION. A superseded
                // pack is EXPECTED to disagree — that is why it was superseded — and
                // marking it red would be the screen contradicting its own vocabulary.
                const agreement = r.is_superseded ? null : chainAgreement(r, chain, t);
                return (
                  <tr key={r.pack_id}>
                    <td>
                      {when(r.issued_at)}
                      <div className="muted">{r.pack_id}</div>
                    </td>
                    <td><StatusChip tone={s.tone} label={s.label} /></td>
                    <td>
                      <div className="muted">
                        {t("{n} record(s)", { n: r.leaf_count })} · {shortHash(r.merkle_root)}
                      </div>
                      {agreement ? <StatusChip tone={agreement.tone} label={agreement.label} /> : null}
                    </td>
                    <td className="numeric">{r.subject_count}</td>
                    <td>
                      <div className="muted">{r.algorithm}</div>
                      <div className="muted">{r.key_id}</div>
                    </td>
                    <td>
                      <a href={`${apiBase}/v1/proof-packs/${r.pack_id}`}>{t("JSON")}</a>
                      {" · "}
                      <a href={`${apiBase}/v1/proof-packs/${r.pack_id}/pdf`}>{t("PDF")}</a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <p className="muted">
          {t("The JSON is what is signed. The PDF is a rendering of it and is never the source of truth. Anyone can verify a pack against the public key without asking us.")}
        </p>
      </section>
    </div>
  );
}
